<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { BanknotesIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import QuoteBillingTable from '@/components/features/quote-billing/QuoteBillingTable.vue'
import { useQuoteBillingDashboardStore } from '@/stores/useQuoteBillingDashboardStore'

const store = useQuoteBillingDashboardStore()

// ── Filtres ──────────────────────────────────────────────────────────────────
function firstDayOfMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
function lastDayOfMonth() {
  const d = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const invoiceDateFrom = ref(firstDayOfMonth())
const invoiceDateTo = ref(lastDayOfMonth())
const signedFrom = ref('')
const signedTo = ref('')
const filterSalesEngineer = ref('')
const filterStatus = ref('')
const search = ref('')
const page = ref(1)
const pageSize = ref(50)

const STATUS_OPTIONS = [
  { id: '', value: '', label: 'Tous les statuts' },
  { id: 'NOT_INVOICED', value: 'NOT_INVOICED', label: 'Non facturé' },
  { id: 'PARTIALLY_INVOICED', value: 'PARTIALLY_INVOICED', label: 'Partiellement facturé' },
  { id: 'INVOICED', value: 'INVOICED', label: 'Facturé' },
  { id: 'UNPAID', value: 'UNPAID', label: 'Non payé' },
  { id: 'PARTIALLY_PAID', value: 'PARTIALLY_PAID', label: 'Partiellement payé' },
  { id: 'PAID', value: 'PAID', label: 'Payé' },
]

const salesEngineerOptions = computed(() => [
  { id: '', value: '', label: 'Tous les commerciaux' },
  ...store.salesEngineers.map((se) => ({ id: se.uuid, value: se.uuid, label: se.label })),
])

// ── Chargement ────────────────────────────────────────────────────────────────
async function refresh() {
  await store.fetchDashboard({
    invoiceDateFrom: invoiceDateFrom.value || undefined,
    invoiceDateTo: invoiceDateTo.value || undefined,
    signedFrom: signedFrom.value || undefined,
    signedTo: signedTo.value || undefined,
    salesEngineerUuid: filterSalesEngineer.value || undefined,
    search: search.value || undefined,
    status: filterStatus.value || undefined,
    page: page.value,
    pageSize: pageSize.value,
  })
}

let searchTimeout: ReturnType<typeof setTimeout> | undefined
function debouncedRefresh() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    refresh()
  }, 400)
}

watch([invoiceDateFrom, invoiceDateTo, signedFrom, signedTo, filterSalesEngineer, filterStatus], () => {
  page.value = 1
  refresh()
})
watch(search, debouncedRefresh)
watch(page, refresh)

onMounted(async () => {
  await Promise.allSettled([store.fetchSalesEngineers(), refresh()])
})

</script>

<template>
  <div class="p-6 space-y-6">

    <!-- Header -->
    <div class="flex items-center justify-between">
      <Bs2Title title="Suivi facturation des devis" :icon="BanknotesIcon" />
      <span class="text-sm text-base-content/50">{{ store.total }} devis</span>
    </div>

    <!-- Filtres -->
    <div class="flex items-end gap-3 flex-wrap">
      <div>
        <label class="label py-0"><span class="label-text text-xs">Recherche</span></label>
        <div class="relative">
          <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-base-content/40" />
          <input
            v-model="search"
            type="text"
            placeholder="Réf. devis, réf. client, client…"
            class="input input-sm input-bordered pl-9 w-64"
          />
        </div>
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Émission facture — du</span></label>
        <input v-model="invoiceDateFrom" type="date" class="input input-sm input-bordered" />
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">au</span></label>
        <input v-model="invoiceDateTo" type="date" class="input input-sm input-bordered" />
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Signature devis — du</span></label>
        <input v-model="signedFrom" type="date" class="input input-sm input-bordered" />
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">au</span></label>
        <input v-model="signedTo" type="date" class="input input-sm input-bordered" />
      </div>
      <SelectInput
        v-model="filterSalesEngineer"
        label="Commercial"
        :options="salesEngineerOptions"
        placeholder="Commercial"
        class="w-56"
      />
      <SelectInput
        v-model="filterStatus"
        label="Statut"
        :options="STATUS_OPTIONS"
        placeholder="Statut"
        class="w-52"
      />
    </div>

    <!-- Loading -->
    <div v-if="store.isLoading" class="flex justify-center py-12">
      <span class="loading loading-spinner loading-lg" />
    </div>

    <!-- Tableau -->
    <QuoteBillingTable v-else :rows="store.rows" />

    <!-- Pagination -->
    <div v-if="store.total > pageSize" class="flex justify-center gap-2">
      <button class="btn btn-sm btn-ghost" :disabled="page <= 1" @click="page--">←</button>
      <span class="btn btn-sm btn-ghost no-animation">Page {{ page }}</span>
      <button class="btn btn-sm btn-ghost" :disabled="page * pageSize >= store.total" @click="page++">→</button>
    </div>

  </div>
</template>
